import React, { useState } from 'react';

import {
  Button,
  BusyButton,
  Dialog,
  DialogContent,
  TextArea,
  useToggle,
  Text,
  Flex,
  Label,
  LssProp
} from '@lucidhq/lucidium';

import { t } from '~/utils/i18n';
import { formatDuplicateIds } from '~/utils/ipMatching';

import { useErrorDialogProvider } from '../Shared/ErrorDialogProvider';
import { useCreateIPMatchingIDs } from '../Shared/hooks/useMutateIPMatching';
import { AddIDsDialogHeader } from './AddIDsDialogHeader';
import { DuplicateIDsDialogContent } from './DuplicateIDsDialogContent';
import { useIPMatchingContext } from './IPMatchingProvider';

type AddIDsDialogProps = {
  isOpen: boolean;
  handleToggle: () => void;
};

const SITE = 'site';
const PLACEMENT = 'placement';
const HIGH = 'high';
const MEDIUM = 'medium';

export const AddIDsDialog = ({ isOpen, handleToggle }: AddIDsDialogProps) => {
  const { campaign, refetch } = useIPMatchingContext();
  const [isSite, handleIDType] = useToggle(false);
  const [newIds, setNewIds] = useState<string>('');
  const [hasDuplicates, setHasDuplicates] = useState<boolean>(false);
  const [duplicateStr, setDuplicateStr] = useState<string>('');
  const { mutate } = useCreateIPMatchingIDs(campaign.campaign_id);
  const { setError } = useErrorDialogProvider();

  const handleTabClick = (isSiteTab: boolean) => {
    if (isSite !== isSiteTab) {
      handleIDType();
    }
  };

  const handleSubmit = () => {
    if (!newIds?.length) return;

    // Split the IDs by new lines and trim each ID
    const parsedIds = newIds.split('\n').map(id => id.trim());
    if (!parsedIds.length) return;

    //filter out empty lines
    const filteredIds = parsedIds.filter(id => id !== '');

    const uniqueIds = [...new Set(filteredIds)];

    setNewIds('');

    const payload = {
      ip_matching_type: `${isSite ? SITE : PLACEMENT}_id`,
      ids: uniqueIds
    };

    mutate(payload, {
      onSuccess: async data => {
        const formattedDuplicatesStr = formatDuplicateIds(data?.errors);

        if (!!formattedDuplicatesStr?.length) {
          setDuplicateStr(formattedDuplicatesStr);
          setHasDuplicates(true);
        } else {
          handleToggle();
        }

        await refetch();
      },
      onError: () => {
        setError(t('errors.generalApiError'));
      }
    });
  };

  const handleClose = () => {
    handleToggle();
    setNewIds('');
    setHasDuplicates(false);
    setDuplicateStr('');
    isSite && handleIDType();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleToggle}
      lss={
        {
          width: '35rem',
          height: '45rem',
          ...(hasDuplicates && {
            width: '27.75rem',
            height: 'fit-content',
            p: 'lg'
          })
        } as LssProp
      }
    >
      {hasDuplicates ? (
        <DuplicateIDsDialogContent
          handleToggle={handleClose}
          duplicateStr={duplicateStr}
        />
      ) : (
        <>
          <AddIDsDialogHeader onClose={handleToggle}>
            {t('ipMatching.actions.addNewIDs')}
          </AddIDsDialogHeader>
          <Flex lss={{ p: 'xl' }}>
            <Button
              type="button"
              isBlock
              scale="lg"
              palette="secondary"
              lss={{
                ...(!isSite && { opacity: 1 })
              }}
              emphasis={!isSite ? HIGH : MEDIUM}
              onClick={() => handleTabClick(false)}
              disabled={!isSite}
            >
              {t('ipMatching.labels.placementIDs')}
            </Button>
            <Button
              type="button"
              isBlock
              scale="lg"
              palette="secondary"
              lss={{
                ...(isSite && { opacity: 1 })
              }}
              emphasis={isSite ? HIGH : MEDIUM}
              onClick={() => handleTabClick(true)}
              disabled={isSite}
            >
              {t('ipMatching.labels.siteIDs')}
            </Button>
          </Flex>
          <Flex lss={{ flexDirection: 'column' }}>
            <DialogContent lss={{ py: 'xxs' }}>
              <Text as="p" lss={{ mt: 'none' }}>
                {t(`ipMatching.actions.${isSite ? SITE : PLACEMENT}`)}
              </Text>
              <Label htmlFor="ip-ids" lss={{ fontWeight: 'bold', mt: 'xxs' }}>
                {isSite
                  ? t('ipMatching.labels.siteIDs')
                  : t('ipMatching.labels.placementIDs')}
              </Label>

              <TextArea
                name="ip-ids"
                placeholder={t('ipMatching.actions.placeholder')}
                onChange={e => setNewIds(e.currentTarget.value)}
                value={newIds}
                rows={25}
              />

              <Flex
                lss={{
                  width: '100%',
                  justifyContent: 'center',
                  mt: 'xxl'
                }}
              >
                <BusyButton
                  type="submit"
                  disabled={!newIds?.length}
                  scale="md"
                  lss={{ mr: 'md' }}
                  palette={!newIds?.length ? 'disabled' : 'secondary'}
                  onClick={handleSubmit}
                >
                  {t('ipMatching.actions.upload')}
                </BusyButton>
                <Button
                  type="button"
                  scale="md"
                  palette="primary"
                  emphasis="medium"
                  onClick={handleClose}
                >
                  {t('ipMatching.actions.cancel')}
                </Button>
              </Flex>
            </DialogContent>
          </Flex>
        </>
      )}
    </Dialog>
  );
};
